import makeDebug from 'debug'

export type ValidateFunction<T, E> = (value: T) => Promise<E | null>

interface FieldState<E> {
  validating: boolean
  valid: boolean
  validationError: E | null
}

type StateChangeListener<E> = (state: FieldState<E>) => void
type Unsubscribe = () => void

interface FieldDef<T, E> {
  getValue(): T
  isFocused(): boolean
  validate: ValidateFunction<T, E>
  debugName?: string | null
}

export interface Field<E> {
  isInvalid(): boolean
  update(): Promise<void>
  validate(): Promise<boolean>
  subscribe(listener: StateChangeListener<E>): Unsubscribe
}

export function createField<T, E>({
  getValue,
  isFocused,
  validate,
  debugName,
}: FieldDef<T, E>): Field<E> {
  const debug = makeDebug(`form-logic:field${debugName ? '/' + debugName : ''}`)

  const initialValue = getValue()
  let previouslyValidatedValue: T | undefined = undefined

  let listeners: StateChangeListener<E>[] = []
  let runningValidations = 0
  let dirty = false

  let state: FieldState<E> = {
    validating: false,
    valid: false,
    validationError: null,
  }

  function notify() {
    debug('notify', state)
    listeners.forEach(listener => listener(state))
  }

  function setState(partial: Partial<FieldState<E>>) {
    state = { ...state, ...partial }
    notify()
  }

  function beginValidate() {
    if (runningValidations === 0) {
      setState({ validating: true })
    }
    runningValidations++
  }

  function endValidate() {
    if (runningValidations === 0) throw new Error('invalid state')
    runningValidations--
    if (runningValidations === 0) {
      setState({ validating: false })
    }
  }

  const isInvalid = () => !!state.validationError

  async function validateValue(value: T) {
    debug('validateValue')

    beginValidate()
    try {
      const error = await validate(value)

      // the value could have changed in the meantime
      if (value !== getValue()) {
        debug('value changed and needs to be evaluated again')
        setState({ valid: false })
        return false
      }

      const valid = error === null

      setState({
        validationError: error,
        valid,
      })

      previouslyValidatedValue = value

      return valid
    } finally {
      endValidate()
    }
  }

  return {
    isInvalid,

    subscribe(listener: StateChangeListener<E>) {
      listeners.push(listener)
      return () => {
        listeners = listeners.filter(l => l !== listener)
      }
    },

    async validate(): Promise<boolean> {
      debug('validate')
      return validateValue(getValue())
    },

    async update() {
      debug('update')

      const value = getValue()

      if (!dirty && value !== initialValue) {
        dirty = true
      }

      if (!dirty) {
        debug('not dirty - no need to validate')
        return
      }

      if (
        typeof previouslyValidatedValue !== 'undefined' &&
        value !== previouslyValidatedValue
      ) {
        // value changed - we don't know if it's valid so let's switch off `valid` flag
        setState({ valid: false })
      }

      if (isFocused() && !isInvalid()) {
        debug('focused and not invalid - no validation')
        return
      }

      await validateValue(value)
    },
  }
}
